import { Dialog } from "primereact/dialog";
import { useDispatch, useSelector } from "react-redux";
import { IPreventiveAppointmentData } from "../../../redux/interfaces/IPreventiveAppointment";
import { useTranslation } from "react-i18next";
import {
  setIsPreventiveAppointmentDetailsPopupOpen,
  clearPreventiveAppointmentPopupData
} from "src/redux/slices/preventiveAppointmentSlice";
import {
  getClassesBasedOnStatus,
  getFieldFieldValue,
  getStatusByStatusId,
  getTextValueById,
  isRtl,
  isCivilDefenseUser,
} from "src/utils/helpers";
import VerticalDivider from "src/components/VerticalDivider/VerticalDivider";
import { useState } from "react";
import { StatusesWithHoldReason } from "src/resources/enum";

const PreventiveAppointmentDetailsPopup = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["common", "assignBuilding"]);

  const language = useSelector((state: any) => state.lookup.language);
  const { appointmentId } = useSelector(
    (state: any) =>
      state.preventiveAppointments.preventiveAppointmentDetailsPopupData
  );
  const pathName = window.location.pathname;
  const [isBreached, setIsBreached] = useState<any>(
    pathName?.includes("breached") ? true : false
  );
  const selectedPreventiveAppointment = useSelector((state: any) =>
    state.preventiveAppointments.preventiveAppointmentData.find(
      (PreventiveAppointment: IPreventiveAppointmentData) =>
        PreventiveAppointment.appointmentId === appointmentId
    )
  );
  const isPreventiveAppointmentsDetailsPopupOpen = useSelector(
    (state: any) =>
      state.preventiveAppointments.isPreventiveAppointmentDetailsPopupOpen
  );

  const {
    appointmentsStatuses,
    citiesOptions,
    emiratesOptions,
    allServiceTerritories,
  } = useSelector((state: any) => state.lookup);

  function closePopup(): void {
    dispatch(setIsPreventiveAppointmentDetailsPopupOpen(false));
    dispatch(clearPreventiveAppointmentPopupData());
  }
  const StatusTemplate = () => {
    const status = getStatusByStatusId(
      appointmentsStatuses,
      selectedPreventiveAppointment?.statusId
    );
    return (
      <span className={`oval ${getClassesBasedOnStatus(status)}`}>
        {getFieldFieldValue(status)}
      </span>
    );
  };
  const getOnHoldReason = () => {
    if (isRtl(language)) return selectedPreventiveAppointment?.onHoldReasonAr;
    else return selectedPreventiveAppointment?.onHoldReasonEn;
  };
  function getEmirate() {
    return getTextValueById(
      selectedPreventiveAppointment?.emirate,
      emiratesOptions,
      "emirateId"
    );
  }

  function getCity() {
    return getTextValueById(
      selectedPreventiveAppointment?.city,
      citiesOptions,
      "cityId"
    );
  }
  const serviceTerritory = getTextValueById(
    selectedPreventiveAppointment?.serviceTerritoryId,
    allServiceTerritories as unknown as [],
    "territoryId"
  );

  function checkIfPendingOwnerApproval() {
    const status = getStatusByStatusId(
      appointmentsStatuses,
      selectedPreventiveAppointment?.statusId
    );
    return status?.englishName?.toLowerCase() === StatusesWithHoldReason.PendingOwnerApproval;
  }
  const isCDUser = isCivilDefenseUser();
  return (
    <Dialog
      visible={isPreventiveAppointmentsDetailsPopupOpen}
      onHide={closePopup}
      header={t("preventiveAppointmentDetails")}
      headerClassName="popup-header"
      className="maintenance-requests-details-popup"
    >
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4 popup-top-section"></div>
        <section>
          <h2 className="font-weight-700">{t("status")}</h2>
          <div className="section-body">
            <div className={`section-body__item small__item ${checkIfPendingOwnerApproval()? "py-0 my-auto" : ""}`}>
              <StatusTemplate />
            </div>
            {checkIfPendingOwnerApproval() && (
              <>
                <VerticalDivider />
                <div className="section-body__item">
                  <h4 className="font-weight-700">{t("typeOfApproval")}</h4>
                  <span>{getOnHoldReason()}</span>
                </div>{" "}
              </>
            )}
          </div>
        </section>
        <section>
          <h2 className="font-weight-700">
            {t("preventiveAppointmentDetails")}
          </h2>
          <div className="section-body">
            <div className="section-body__item small__item">
              <h4 className="font-weight-700">{t("appointmentId")}</h4>
              <span>{selectedPreventiveAppointment?.appointmentIdNumber}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item small__item">
              <h4 className="font-weight-700">{t("appointmentDate")}</h4>
              <span>{selectedPreventiveAppointment?.appointmentDate}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item">
              <h4 className="font-weight-700">{t("appointmentType")}</h4>
              <span>
                {isRtl(language)
                  ? selectedPreventiveAppointment?.appointmentTypeAr
                  : selectedPreventiveAppointment?.appointmentTypeEn}
              </span>
            </div>
          </div>
          <div className="section-body">
            <div className="section-body__item small__item">
              <h4 className="font-weight-700">{t("creationDate")}</h4>
              <span>{selectedPreventiveAppointment?.creationDate}</span>
            </div>
            {!isBreached && (
              <>
                {" "}
                <VerticalDivider />
                <div className="section-body__item small__item">
                  <h4 className="font-weight-700">{t("completionDate")}</h4>
                  <span>{selectedPreventiveAppointment?.completionDate}</span>
                </div>
              </>
            )}
            {!isCDUser && (
              <>
                <VerticalDivider />
                <div className="section-body__item small__item">
                  <h4 className="font-weight-700">
                    {t("managerSupervisorName")}
                  </h4>
                  <span>
                    {selectedPreventiveAppointment?.managerSupervisorName}
                  </span>
                </div>
              </>
            )}
          </div>
        </section>

        <section>
          <h2 className="font-weight-700">
            {isCDUser ? t("bmcAndPropertyDetails") : t("propertyDetails")}
          </h2>
          <div className="section-body">
            <div className="section-body__item w-32 w-md-100">
              <h4 className="font-weight-700">{t("propertyID")}</h4>
              <span>{selectedPreventiveAppointment?.propertyNumber}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item w-32 w-md-100">
              <h4 className="font-weight-700">{t("buildingNameHeader")}</h4>
              <span>{selectedPreventiveAppointment?.buildingName}</span>
            </div>

            {isCDUser ? (
              <>
                <VerticalDivider />
                <div className="section-body__item w-15 w-md-100">
                  <h4 className="font-weight-700">{t("bmcCompanyName")}</h4>
                  <span>{selectedPreventiveAppointment?.companyName}</span>
                </div>
                <VerticalDivider />
                <div className="section-body__item w-15 w-md-100">
                  <h4 className="font-weight-700">{t("emirate")}</h4>
                  <span>{getEmirate()}</span>
                </div>
              </>
            ) : (
              <>
                <VerticalDivider />
                <div className="section-body__item w-32 w-md-100">
                  <h4 className="font-weight-700">{t("emirate")}</h4>
                  <span>{getEmirate()}</span>
                </div>
              </>
            )}
          </div>
          <div className="section-body">
            <div className="section-body__item w-32 w-md-100">
              <h4 className="font-weight-700">{t("city")}</h4>
              <span>{getCity()}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item w-32 w-md-100">
              <h4 className="font-weight-700">{t("territory")}</h4>
              <span>{serviceTerritory}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item w-15 w-md-100">
              <h4 className="font-weight-700">{t("latitude")}</h4>
              <span>{selectedPreventiveAppointment?.latitude}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item w-15 w-md-100">
              <h4 className="font-weight-700">{t("longitude")}</h4>
              <span>{selectedPreventiveAppointment?.longitude}</span>
            </div>
          </div>
        </section>
      </div>
    </Dialog>
  );
};

export default PreventiveAppointmentDetailsPopup;
